import { useEffect } from "react";

/**
 * A custom hook to handle keyboard shortcuts, including key combinations like "Ctrl + P".
 *
 * @param keys - An array of keys to trigger the shortcut (e.g., ["Control", "p"]).
 * @param callback - The function to execute when the keys are pressed.
 */
export const useShortcut = (keys: string[], callback: () => void, preventDefault: boolean = true): void => {
  useEffect(() => {
    const pressedKeys = new Set<string>(); // Track pressed keys

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!event.key) return; // Add safety check
      pressedKeys.add(event.key.toLowerCase());

      // Check if all keys in the combination are pressed
      const allKeysPressed = keys.every((key) =>
        pressedKeys.has(key.toLowerCase())
      );

      if (allKeysPressed) {
        if (preventDefault) {
          event.preventDefault();
        }
        callback();
      }
    };

    const handleKeyUp = (event: KeyboardEvent): void => {
      if (!event.key) return; // Add safety check
      pressedKeys.delete(event.key.toLowerCase());
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [keys, callback]);
};
